import React, { useState } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { ChevronDown, LogOut } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const TopBarContainer = styled.div`
  height: 64px;
  background-color: #FFFFFF;
  border-bottom: 1px solid #E5E7EB;
  position: fixed;
  top: 0;
  left: 250px;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 0 2rem;
  font-family: 'Prompt', sans-serif;
  z-index: 999;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
`;

const RightSection = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
`;

const NotificationButton = styled.button`
  position: relative;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 0.375rem;
  transition: background-color 0.2s;

  &:hover {
    background-color: #F3F4F6;
  }

  svg {
    width: 1.25rem;
    height: 1.25rem;
    color: #6B7280;
  }
`;

const NotificationBadge = styled.div`
  position: absolute;
  top: 0.25rem;
  right: 0.25rem;
  width: 0.5rem;
  height: 0.5rem;
  background-color: #EF4444;
  border-radius: 50%;
  border: 2px solid #FFFFFF;
`;

const UserSection = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 0.375rem;
  transition: background-color 0.2s;

  &:hover {
    background-color: #F3F4F6;
  }
`;

const UserAvatar = styled.div`
  width: 2rem;
  height: 2rem;
  background-color: #06B6D4;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #FFFFFF;
  font-weight: 600;
  font-size: 0.875rem;
`;

const UserDetails = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const UserName = styled.span`
  font-size: 0.875rem;
  font-weight: 500;
  color: #111827;
  line-height: 1.2;
`;

const UserRole = styled.span`
  font-size: 0.75rem;
  color: #6B7280;
  line-height: 1.2;
`;

const DropdownButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 0.25rem;
  transition: background-color 0.2s;

  &:hover {
    background-color: #F3F4F6;
  }

  svg {
    width: 1rem;
    height: 1rem;
    color: #6B7280;
    transition: transform 0.2s;
    transform: ${props => props.isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
  }
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 0.5rem;
  background-color: #FFFFFF;
  border: 1px solid #E5E7EB;
  border-radius: 0.5rem;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
  padding: 0.5rem 0;
  min-width: 180px;
  z-index: 1001;
  opacity: ${props => props.isOpen ? 1 : 0};
  visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
  transform: ${props => props.isOpen ? 'translateY(0)' : 'translateY(-10px)'};
  transition: all 0.2s ease;
`;

const DropdownItem = styled.button`
  width: 100%;
  text-align: left;
  background: none;
  border: none;
  padding: 0.75rem 1rem;
  font-size: 0.875rem;
  color: #374151;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  transition: background-color 0.2s;

  &:hover {
    background-color: #F3F4F6;
  }

  svg {
    width: 1rem;
    height: 1rem;
    color: #6B7280;
  }

  &.logout {
    color: #DC2626;
    border-top: 1px solid #E5E7EB;
    margin-top: 0.25rem;
    
    svg {
      color: #DC2626;
    }
  }
`;

const TopBar = ({ user: propUser, onLogout }) => {
  const navigate = useNavigate();
  const { user: authUser, logout, getUserFullName } = useAuth();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  // ใช้ user จาก AuthContext หรือ props
  const user = authUser || propUser;

  const getFullName = () => {
    if (!user) return '-';

    if (user.fullName) return user.fullName;

    if (getUserFullName) return getUserFullName();

    const parts = [];
    if (user.nameprefix) parts.push(user.nameprefix);
    if (user.name) parts.push(user.name);
    if (user.lastname) parts.push(user.lastname);

    return parts.length > 0 ? parts.join(' ') : (user.username || '-');
  };

  const fullName = getFullName();

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleLogout = async () => {
    setIsDropdownOpen(false);
    await logout();
    if (onLogout) onLogout();
    navigate('/login');
  };

  // ปิด dropdown เมื่อคลิกข้างนอก
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (isDropdownOpen && !event.target.closest('.user-dropdown')) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [isDropdownOpen]);

  return (
    <TopBarContainer>
      <RightSection>
        <UserSection className="user-dropdown">
          <UserInfo onClick={toggleDropdown}>
            <UserAvatar>
              {fullName?.charAt(0) || 'A'}
            </UserAvatar>
            <UserDetails>
              <UserName>{fullName}</UserName>
            </UserDetails>
          </UserInfo>

          <DropdownButton
            isOpen={isDropdownOpen}
            onClick={toggleDropdown}
          >
            <ChevronDown />
          </DropdownButton>

          <DropdownMenu isOpen={isDropdownOpen}>
            <DropdownItem className="logout" onClick={handleLogout}>
              <LogOut />
              ออกจากระบบ
            </DropdownItem>
          </DropdownMenu>
        </UserSection>
      </RightSection>
    </TopBarContainer>
  );
};

export default TopBar;